import React, { useContext } from "react";
import { Link } from "react-router-dom";
import { FiArrowRight, FiHeart } from "react-icons/fi";
import { CartContext } from "./CartContext";
import productData from "../Datas/productData";
import { formatPrice } from "../config/siteConfig";
import "../css/main.scss";

function Main() {
  const { isWishlisted, toggleWishlist } = useContext(CartContext);
  const newArrivals = productData.filter((product) => product.collection === "new");
  const limitedPicks = productData.filter((product) => product.collection === "limited");
  const restocks = productData.filter((product) => product.collection === "restock");
  const topStories = [
    { product: productData[0], image: "/main/top-journal.jpg" },
    { product: productData[3], image: "/main/top-washi.jpg" },
    { product: productData[10], image: "/main/top-pen.jpg" },
    { product: productData[19], image: "/main/top-scissors.jpg" },
  ];
  const categories = [
    { title: "Tapes & Stickers", copy: "Layers of colour and small illustrated details.", image: "/main/category-tapes.jpg" },
    { title: "Stamps", copy: "Tiny marks that make every page unmistakably yours.", image: "/main/category-stamps.jpg" },
    { title: "Writing & Drawing", copy: "Comfortable tools for lines, letters and ideas.", image: "/main/category-writing.jpg" },
    { title: "Notebooks & Journals", copy: "Good paper for slow thoughts and daily plans.", image: "/main/category-notebooks.jpg" },
    { title: "Paper Goods", copy: "Letters and cards worth sending by hand.", image: "/main/category-paper.jpg" },
    { title: "Desk Essentials", copy: "Quietly useful objects for a thoughtful workspace.", image: "/main/category-desk.jpg" },
  ];

  const renderProductCollection = (title, products, query) => (
    <section className="home-products section-space" aria-labelledby={`section-${query}`}>
      <div className="section-heading section-heading--center">
        <p className="eyebrow">Curated for your desk</p>
        <h2 id={`section-${query}`}>{title}</h2>
      </div>
      <div className="product-row">
        {products.slice(0, 5).map((product) => (
          <article className="product-card" key={product.id}>
            <div className="product-card__media">
              <Link to={`/shopDetails/${product.id}`} aria-label={product.name}>
                <img
                  src={product.image}
                  alt={product.name}
                  loading="lazy"
                  decoding="async"
                  width="900"
                  height="900"
                />
              </Link>
              {product.compareAtPrice && <span className="product-badge">Sale</span>}
              <button
                type="button"
                className={`wishlist-button${isWishlisted(product.id) ? " is-active" : ""}`}
                onClick={() => toggleWishlist(product.id)}
                aria-label={`${isWishlisted(product.id) ? "Remove" : "Add"} ${product.name} ${isWishlisted(product.id) ? "from" : "to"} wishlist`}
              >
                <FiHeart />
              </button>
            </div>
            <div className="product-card__content">
              <p>{product.brand}</p>
              <h3><Link to={`/shopDetails/${product.id}`}>{product.name}</Link></h3>
              <div className="product-card__price">
                {product.compareAtPrice && <s>{formatPrice(product.compareAtPrice)}</s>}
                <span>{formatPrice(product.price)}</span>
              </div>
            </div>
          </article>
        ))}
      </div>
      <div className="section-action">
        <Link className="text-link" to={`/shop?collection=${query}`}>View all <FiArrowRight /></Link>
      </div>
    </section>
  );

  return (
    <main className="main home-page">
      <section className="home-hero">
        <img
          src="/main/hero-studio.jpg"
          alt="A calm sunlit journaling desk with paper and writing tools"
          width="2048"
          height="1024"
        />
        <div className="home-hero__shade" aria-hidden="true" />
        <div className="home-hero__content">
          <p className="eyebrow">New season · quiet beginnings</p>
          <h1>Make room for<br />your own story.</h1>
          <p>Original stationery chosen for the feel of paper, the ease of a good pen and the joy of daily use.</p>
          <Link className="button button--light" to="/shop?collection=new">Discover new arrivals</Link>
        </div>
      </section>

      <section className="top-arrivals page-shell section-space" aria-labelledby="top-arrivals-title">
        <div className="section-heading section-heading--center">
          <p className="eyebrow">A first look</p>
          <h2 id="top-arrivals-title">Top New Arrivals</h2>
        </div>
        <div className="top-arrivals__grid">
          {topStories.map(({ product, image }) => (
            <Link key={product.id} to={`/shopDetails/${product.id}`}>
              <span className="top-arrivals__image">
                <img src={image} alt="" loading="lazy" decoding="async" width="720" height="720" />
              </span>
              <span>{product.name}</span>
            </Link>
          ))}
        </div>
        <Link className="button button--outline" to="/shop?collection=new">View all new arrivals</Link>
      </section>

      <section className="brand-intro page-shell">
        <span className="brand-intro__lines" aria-hidden="true">{"///"}</span>
        <p>
          Stationery is not a relic of the analogue age—it comes alive in our hands, in daily use.
          We hope you find pieces that resonate with you and discover their quiet magic in your own way.
        </p>
        <span className="brand-intro__pen" aria-hidden="true" />
      </section>

      {renderProductCollection("New Arrivals", newArrivals, "new")}
      {renderProductCollection("Limited-time Picks", limitedPicks, "limited")}
      {renderProductCollection("Restocks", restocks, "restock")}

      <section className="story-banner section-space">
        <img
          src="/main/story-desk.jpg"
          alt="An open journal and creative stationery arranged on a warm desk"
          loading="lazy"
          decoding="async"
          width="2048"
          height="1024"
        />
        <div>
          <p>“In the soft whisper of pen and paper, listen to your own story unfold.”</p>
          <Link className="text-link text-link--light" to="/about">Our story <FiArrowRight /></Link>
        </div>
      </section>

      <section className="category-edit page-shell section-space" aria-labelledby="category-title">
        <div className="section-heading section-heading--center">
          <p className="eyebrow">Find your tools</p>
          <h2 id="category-title">Browse by collection</h2>
        </div>
        <div className="category-edit__grid">
          {categories.map(({ title, copy, image }) => (
            <article key={title}>
              <Link to={`/shop?category=${encodeURIComponent(title)}`}>
                <img src={image} alt="" loading="lazy" decoding="async" width="720" height="720" />
              </Link>
              <h3><Link to={`/shop?category=${encodeURIComponent(title)}`}>{title}</Link></h3>
              <p>{copy}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="journal-notes page-shell section-space" aria-labelledby="notes-title">
        <div className="section-heading">
          <p className="eyebrow">Paper notes</p>
          <h2 id="notes-title">From the journal</h2>
        </div>
        <div className="journal-notes__grid">
          <article>
            <span>01</span>
            <div><p>Guide</p><h3>How to begin a five-minute journaling ritual</h3><Link to="/about">Read the story <FiArrowRight /></Link></div>
          </article>
          <article>
            <span>02</span>
            <div><p>Materials</p><h3>Finding paper that feels right beneath your pen</h3><Link to="/about">Read the story <FiArrowRight /></Link></div>
          </article>
          <article>
            <span>03</span>
            <div><p>Studio visit</p><h3>The small objects that make a desk feel like home</h3><Link to="/about">Read the story <FiArrowRight /></Link></div>
          </article>
        </div>
      </section>
    </main>
  );
}

export default Main;
